import { useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Flex, Space } from "antd";
import { AppParagraph, GroupTag } from "@/shared/Components";
import { CakeIcon } from "@/shared/icons";
import { getAge } from "@/common/PersonInfoCard/helpers";
import { handleFullName, transformSDateToString } from "@/shared/utils";
import type { Person } from "@/services/api";
import type { Group } from "@/shared/Components";
import styles from "./styles.module.scss";

export const InfoView = ({ currentPerson }: { currentPerson: Person }) => {
  const { t } = useTranslation();

  const { birthdaySDate, gender } = currentPerson;

  const ageAndGender = useMemo(() => {
    const age = getAge(birthdaySDate);

    const parsedGender = gender && gender !== "NOT_DEFINED" && t(`common.${gender.toLowerCase()}`);

    const parsedAge = !!age && `${age} ${t("common.years")}`;

    return [parsedGender, parsedAge].filter(Boolean).join(", ") || undefined;
  }, [birthdaySDate, gender]);

  const birthday = transformSDateToString(currentPerson?.birthdaySDate);

  return (
    <Flex className={classNames(styles.info, styles[`${"desktop"}`])} justify="flex-start">
      {currentPerson.group ? (
        <GroupTag group={currentPerson.group as Group} className={styles.tag} />
      ) : (
        <div className={styles["tag-empty"]} />
      )}
      <Space direction="vertical">
        <AppParagraph size="xs" weight="medium" lineHeight="sm" className={styles.work}>
          {currentPerson.title}
        </AppParagraph>
        <AppParagraph size="lg" weight="semibold" lineHeight="xl" className={styles.name}>
          {handleFullName(
            currentPerson.firstName,
            currentPerson.lastName,
            currentPerson.middleName,
          )}
        </AppParagraph>
        {ageAndGender && (
          <AppParagraph
            size="xs"
            weight="semibold"
            lineHeight="sm"
            className={styles["age-and-gender"]}
          >
            {ageAndGender}
          </AppParagraph>
        )}
      </Space>
      {birthday ? (
        <div className={styles["birthday-container"]}>
          <CakeIcon />
          <AppParagraph size="sm" className={styles.birthday}>
            {birthday}
          </AppParagraph>
        </div>
      ) : (
        <div className={styles["birthday-empty"]} />
      )}
    </Flex>
  );
};
